// "Other jobs for you" — shown above My Applications when an employer answered
// "Not a fit this time" (status 'rejected'). The applicant's alert says "we found other open
// jobs that match you"; this is where they land. Same matcher as the after-apply step
// (GET /api/applications/next-jobs), excluding the job that turned them down. Hides itself
// when there is nothing to show.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, MapPin } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { supabase } from '../lib/supabase';
import api from '../lib/api';

const formatPay = (job) => {
  const min = job.salary_min ? `₱${Number(job.salary_min).toLocaleString()}` : '';
  const max = job.salary_max ? `₱${Number(job.salary_max).toLocaleString()}` : '';
  const range = min && max ? `${min}-${max}` : min || max;
  if (!range) return '';
  return job.salary_period ? `${range}/${job.salary_period}` : range;
};

export default function OtherJobsForYou({ applications = [], t }) {
  const [jobs, setJobs] = useState([]);

  // Only a recent "not this time" (14 days) — the card must not linger on the dashboard forever.
  const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
  const latestRejected = applications
    .filter(a => a.type === 'application' && a.status === 'rejected' && a.job_id
      && new Date(a.updated_at || a.created_at).getTime() >= cutoff)
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))[0];
  const excludeId = latestRejected?.job_id;

  useEffect(() => {
    if (!excludeId) {
      setJobs([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;
        const response = await api.get('/api/applications/next-jobs', {
          params: { exclude: excludeId, limit: 3 },
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        if (!cancelled) setJobs(response.data?.jobs || []);
      } catch (error) {
        if (!cancelled) setJobs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [excludeId]);

  if (!excludeId || jobs.length === 0) return null;

  return (
    <Card className="mb-4 border-primary/30" data-testid="other-jobs-for-you">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} className="text-primary flex-shrink-0" />
          <h3 className="font-semibold">{t('dashboard.otherJobsTitle', 'Other open jobs that match you')}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          {t('dashboard.otherJobsDesc', 'One employer is not moving forward this time. These jobs are still hiring.')}
        </p>
        <ul className="divide-y">
          {jobs.map(job => (
            <li key={job.id}>
              <Link
                to={job.slug ? `/jobs/${job.slug}` : `/jobs/${job.id}`}
                className="flex items-center justify-between gap-3 py-2 hover:bg-muted/50 rounded-lg px-1"
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{job.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 min-w-0">
                    {job.employer_name && <span className="truncate">{job.employer_name}</span>}
                    {job.location && (
                      <span className="flex items-center gap-0.5 truncate"><MapPin size={11} />{job.location}</span>
                    )}
                  </div>
                  {formatPay(job) && <div className="text-xs text-green-700">{formatPay(job)}</div>}
                </div>
                <span className="text-xs font-medium text-primary whitespace-nowrap">{t('dashboard.viewJob', 'View job')}</span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
